// src/pages/BulkImport.js
import React, { useState, useContext } from "react";
import { Importer, ImporterField } from "react-csv-importer";
import "react-csv-importer/dist/index.css";
import { request } from "../utils/request";
import { ColorModeContext } from "../index";  // ← your theme context

const BulkImport = () => {
  const [importedData, setImportedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Read light/dark mode
  const { mode } = useContext(ColorModeContext);
  const textColor = mode === "dark" ? "#ffffff" : "#000000";

  // The fields that exist in your MongoDB schema (without _id, createdAt, etc.)
  const allowedFields = [
    "serviceName",
    "modelName",
    "oemName",
    "serialNumber",
    "assetTags",
    "lastUpdated",
    "currentVersion",
    "status",
    "ipaddress",
    "macaddress",
  ];

  // This function is called when the CSV Importer finishes its internal parsing.
  // We'll build an array of row objects from preview.columns.
  const handleImportComplete = (results) => {
    console.log("Imported CSV Data:", results);

    // Ensure the data is in the shape we expect
    if (results && results.preview && Array.isArray(results.preview.columns)) {
      const { columns } = results.preview;
      const rowCount = columns[0]?.values?.length || 0;
      const rows = [];

      // Build each row as an object
      for (let i = 0; i < rowCount; i++) {
        const rowObj = {};

        columns.forEach((col) => {
          // Only include columns whose header is in our allowedFields array
          if (allowedFields.includes(col.header)) {
            rowObj[col.header] = col.values[i] || "";
          }
        });

        rows.push(rowObj);
      }

      setImportedData(rows);
      setMessage(`Parsed ${rowCount} row(s) from CSV.`);
      console.log("Parsed row objects:", rows);
    } else {
      setMessage("No valid data found in the CSV.");
    }
  };

  // This function sends the parsed data to your backend
  const handleSubmit = async () => {
    if (importedData.length === 0) {
      setMessage("No data to import.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const data = await request("/addfirmware", {
        method: "POST",
        body: importedData,
      });

      if (data) {
        setMessage("Data imported successfully!");
        setImportedData([]); // Optionally clear out the data
      } else {
        setMessage("Error importing data. Please try again.");
      }
    } catch (error) {
      console.error("Error importing data:", error);
      setMessage("Error importing data. Please try again.");
    }

    setLoading(false);
  };

  // Combined container style
  const containerStyle = {
    padding: "20px",
    maxWidth: "850px",
    margin: "0 auto",
    width: "1080px",
    minWidth: "1080px",
    overflow: "hidden",
    color: textColor,
  };

  return (
    <div className="bulk-import-container" style={containerStyle}>
      {/* Scoped CSS to force all text/links/inputs/buttons white in dark mode */}
      <style>{`
        .bulk-import-container a,
        .bulk-import-container p,
        .bulk-import-container h2,
        .bulk-import-container button,
        .bulk-import-container label,
        .bulk-import-container .rci__field-label,
        .bulk-import-container .rci__row-preview,
        .bulk-import-container .rci__column-preview,
        .bulk-import-container .rci__column-actions {
          color: ${textColor} !important;
        }
        .bulk-import-container input,
        .bulk-import-container textarea {
          color: ${textColor} !important;
        }
        .bulk-import-container button {
          background: none;
          border: 1px solid ${textColor};
        }
      `}</style>

      <h2>Bulk Import CSV Data</h2>

      <div style={{ width: "100%", minWidth: "850px", overflow: "hidden" }}>
        <Importer
          assumeNoHeaders={false}
          restartable={true}
          onComplete={handleImportComplete}
          onError={(error) => {
            console.error("Error processing CSV:", error);
            setMessage("Error processing CSV file.");
          }}
        >
          <ImporterField name="serviceName" label="Service Name" optional />
          <ImporterField name="modelName" label="Model Name" optional />
          <ImporterField name="oemName" label="OEM Name" optional />
          <ImporterField name="serialNumber" label="Serial Number" optional />
          <ImporterField name="assetTags" label="Asset Tags" optional />
          <ImporterField name="lastUpdated" label="Last Updated" optional />
          <ImporterField name="currentVersion" label="Current Version" optional />
          <ImporterField name="status" label="Status" optional />
          <ImporterField name="ipaddress" label="IP Address" optional />
          <ImporterField name="macaddress" label="MAC Address" optional />
        </Importer>
      </div>

      <div style={{ marginTop: "20px" }}>
        <button onClick={handleSubmit} disabled={loading}>
          {loading ? "Importing..." : "Import"}
        </button>
      </div>

      {message && <p style={{ marginTop: "10px" }}>{message}</p>}
    </div>
  );
};

export default BulkImport;
